import { CardContent, Switch } from "@libs/zapi-ui/shadcn";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { CheckIcon, XIcon } from "@libs/zapi-ui/assets";
import { useCallback, useMemo, useState } from "react";
import { AppButton, ProseWrapper } from "@libs/zapi-ui/custom";
import { useParams } from "react-router-dom";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { HighlightedDiff2 } from "./_HighlightedDiff2";
import { useDiffStore } from "@/zapi-frontend/stores/diff";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { markdownToHtmlSync } from "@/zapi-frontend/utils/markdown";
import { hasPendingDiffs } from "@/zapi-frontend/utils/diff-utils";

export function DiffViewTab({ className }: { className?: string }) {
  const { id } = useParams();
  const [showDiffs, setShowDiffs] = useState(true);
  const projectId = typeof id === "string" ? id : "";
  const { activeChapter } = useChapterStore();
  const chapterId = activeChapter?.id?.toString() || "";
  const { setContent } = useEditorStore();
  const { getDiffEditor, acceptChanges, rejectChanges, getAcceptedVersion } = useDiffStore();
  const htmlContent = getDiffEditor(projectId, chapterId)?.htmlContent;
  const hasDiffs = !!htmlContent && hasPendingDiffs(htmlContent);
  const acceptedVersion = getAcceptedVersion(projectId, chapterId);
  const diffDoneCallback = useCallback(
    (content: string) => {
      setContent?.(content);
    },
    [setContent]
  );
  const acceptedHtml = useMemo(() => (acceptedVersion ? markdownToHtmlSync(acceptedVersion) : ""), [acceptedVersion]);

  if (!acceptedVersion || !hasDiffs) return null;

  // Both views stay mounted: remounting the TipTap editor or re-injecting the HTML repaints
  // from empty and reloads every image, which shows as a blink on each toggle.
  return (
    <CardContent className={"p-4 overflow-y-auto h-full " + className}>
      <div className="space-y-4 flex flex-col h-full">
        <HighlightedDiff2 diffDoneCallback={diffDoneCallback} className={showDiffs ? undefined : "hidden"} />
        <ProseWrapper className={cn("max-h-[calc(100vh-124px-56px-40px-48px)]", showDiffs && "hidden")}>
          <div dangerouslySetInnerHTML={{ __html: acceptedHtml }}></div>
        </ProseWrapper>
        <div className="flex space-x-2 rounded-lg shadow-sm border-1 p-2 items-center">
          <AppButton
            appearance="text"
            onClick={() => {
              const chapterId = activeChapter?.id?.toString();
              if (!chapterId) return;
              acceptChanges(projectId, chapterId, diffDoneCallback);
            }}
          >
            <CheckIcon />
            Accept all
          </AppButton>
          <AppButton
            appearance="text"
            onClick={() => {
              const chapterId = activeChapter?.id?.toString();
              if (!chapterId) return;
              rejectChanges(projectId, chapterId, diffDoneCallback);
            }}
          >
            <XIcon /> Reject all
          </AppButton>
          <div className="flex ml-auto gap-2 items-center">
            <div className="flex rounded-sm overflow-hidden">
              <span className="bg-green-100 h-[20px] w-[20px]" />
              <span className="bg-red-100  h-[20px] w-[20px]" />
            </div>
            Show diffs
            <Switch
              className="h-5 w-8"
              checked={showDiffs}
              onClick={() => {
                setShowDiffs((prev) => !prev);
              }}
            />
          </div>
        </div>
      </div>
    </CardContent>
  );
}
